import { useState } from "react";

import { Button, Dialog, DialogClose, Field, Input, Notice, Select, Textarea, plainError } from "../../../../components/ui";
import { businessApi } from "../../../../services/api";

const FREQUENCIES = [
  ["semi_monthly", "Twice a month"],
  ["bi_weekly", "Every two weeks"],
  ["weekly", "Every week"],
];

const TEXT = {
  create: { title: "New pay schedule", description: "Saved as a draft. A draft pays nobody; the business owner activates it.", submit: "Save draft" },
  edit: { title: "Edit pay schedule", description: "", submit: "Save changes" },
  successor: { title: "Replace with a new pay schedule", description: "A pay schedule that has been used keeps its rule, because its past periods depend on it. The new rule is saved as a draft that replaces this one; activate it, move the employees to it, then archive this one.", submit: "Save replacement draft" },
};

/**
 * Draft, edit or replace a pay schedule (Payroll Gate 3). C1: a draft is inert and the form is its own confirmation.
 * A schedule that has been used only takes a new name and notes (the server says so too); a new rule is a
 * replacement (`successor`, the owner's). The server validates everything again, including how far apart pay dates
 * fall, and its refusal stays in the form.
 */
export default function ScheduleFormDialog({ mode = "create", schedule = null, used = false, onClose, onSaved }) {
  const text = TEXT[mode];
  const ruleLocked = mode === "edit" && used;
  const start = mode === "successor" ? { ...schedule, name: `${schedule?.name || ""} (new)` } : schedule;
  const [form, setForm] = useState(() => ({
    name: start?.name || "",
    frequency: start && start.frequency !== "monthly" ? start.frequency : "semi_monthly",
    cutoff: String(start?.semiMonthlyCutoffDay ?? 15),
    anchor: start?.anchorDate || "",
    delay: String(start?.payDelayDays ?? 5),
    notes: start?.notes || "",
  }));
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const validate = () => {
    const next = {};
    const name = form.name.replace(/\s+/g, " ").trim();
    if (name.length < 2 || name.length > 120) next.name = "Give it a name of 2 to 120 characters.";
    if (!ruleLocked) {
      const delay = Number(form.delay);
      if (!Number.isInteger(delay) || delay < 0 || delay > 15) next.delay = "Enter a whole number of days from 0 to 15.";
      if (form.frequency === "semi_monthly") {
        const cut = Number(form.cutoff);
        if (!Number.isInteger(cut) || cut < 1 || cut > 27) next.cutoff = "Enter a day from 1 to 27.";
      } else if (!form.anchor) {
        next.anchor = "Choose the first day of one pay period; every later period is counted from it.";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy || !validate()) return;
    setBusy(true);
    setFailure("");
    const name = form.name.replace(/\s+/g, " ").trim();
    const notes = form.notes.trim() || null;
    const rule = {
      frequency: form.frequency,
      payDelayDays: Number(form.delay),
      ...(form.frequency === "semi_monthly" ? { semiMonthlyCutoffDay: Number(form.cutoff) } : { anchorDate: form.anchor }),
    };
    try {
      let res;
      if (mode === "create") res = await businessApi.createPaySchedule({ name, notes, ...rule });
      else if (mode === "successor") res = await businessApi.createPayScheduleSuccessor(schedule.id, { name, notes, ...rule });
      else res = await businessApi.updatePaySchedule(schedule.id, { name, notes, rowVersion: schedule.rowVersion, ...(ruleLocked ? {} : rule) });
      onSaved(res?.data || res);
    } catch (error) {
      setFailure(plainError(error, "The pay schedule couldn't be saved. Try again."));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      size="lg"
      onOpenChange={(open) => { if (!open && !busy) onClose(); }}
      title={text.title}
      description={ruleLocked ? "This pay schedule has been used, so only its name and notes can change. A different cadence, cutoff or pay date is a replacement schedule." : text.description}
      footer={
        <>
          <DialogClose asChild><Button disabled={busy}>Cancel</Button></DialogClose>
          <Button variant="primary" type="submit" form="pay-schedule-form" loading={busy}>{text.submit}</Button>
        </>
      }
    >
      <form id="pay-schedule-form" noValidate onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" required error={errors.name} className="sm:col-span-2">
          <Input maxLength={120} value={form.name} disabled={busy} onChange={set("name")} />
        </Field>
        {ruleLocked ? null : (
          <>
            <Field label="How often payroll runs" required>
              <Select value={form.frequency} disabled={busy} onChange={set("frequency")}>
                {FREQUENCIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Select>
            </Field>
            {form.frequency === "semi_monthly" ? (
              <Field label="First period ends on day" required error={errors.cutoff} hint="The first period runs from the 1st to this day; the second from the next day to month end.">
                <Input type="number" inputMode="numeric" min={1} max={27} step={1} value={form.cutoff} disabled={busy} onChange={set("cutoff")} />
              </Field>
            ) : (
              <Field label="First day of one pay period" required error={errors.anchor} hint="Any past or future period start; every period is counted from it.">
                <Input type="date" value={form.anchor} disabled={busy} onChange={set("anchor")} />
              </Field>
            )}
            <Field label="Days from cutoff to pay date" required error={errors.delay} hint="Pay dates are never more than 16 days apart.">
              <Input type="number" inputMode="numeric" min={0} max={15} step={1} value={form.delay} disabled={busy} onChange={set("delay")} />
            </Field>
          </>
        )}
        <Field label="Notes (optional)" className="sm:col-span-2">
          <Textarea rows={2} maxLength={1000} value={form.notes} disabled={busy} onChange={set("notes")} />
        </Field>
        {failure ? <Notice tone="danger" title="Not saved" role="alert" className="sm:col-span-2">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}
